// @ts-ignore
import {CurlHelper} from "axios-curlirize/src/lib/CurlHelper.js";
import {DialogsReduxSliceStateSubmitTicketEntryData} from "../../../redux/slices/dialogs";
import React, {Dispatch, FunctionComponent, SetStateAction, useCallback, useMemo, useRef, useState} from "react";
import {FinalForm, Form, MuiRFF, useIsMounted, Yup} from "../../../index";
import {ApiService, AuthenticationService, EnvService, Utils} from "../../../../core";
import {Box, Button, DialogActions, DialogContent, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {SubmitTicketDialogSubmissionResult} from "../index";

export interface SubmitTicketFormProps {
    data: DialogsReduxSliceStateSubmitTicketEntryData,
    setSubmissionResult: Dispatch<SetStateAction<SubmitTicketDialogSubmissionResult | undefined>>,
}

const SubmitTicketForm: FunctionComponent<SubmitTicketFormProps> = (props) => {
    const {setSubmissionResult} = props;

    const [submitting, setSubmitting] = useState(false);
    const formRef = useRef<FinalForm.FormApi<Schema, Partial<Schema>>>();
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object({
        description: Yup.string()
            .optional()
            .trim(),
        takeScreenshot: Yup.boolean()
            .default(false),
    }), []);

    interface Schema extends Yup.InferType<typeof schema> {
    }

    type SchemaKeys = keyof Schema;

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo<Record<SchemaKeys, boolean>>(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo<Partial<Schema>>(() => ({
        description: '',
        takeScreenshot: false,
    }), [])

    /**
     * Generates a C-URL from the response or request of the Axios.
     *
     * @param request
     * @param response
     */
    const generateCurl = useCallback(() => {
        return new CurlHelper(props.data.requestModel).generateCommand();
    }, [props.data.requestModel])

    /**
     * Removes the current dialog from the viewport then takes its screenshot and then brings back the dialog.
     * @param allowExplicitScreenshot            Whether to take a screenshot or not.
     * @return                                   The screenshot taken.
     */
    const takeScreenshot = useCallback(async (allowExplicitScreenshot: boolean): Promise<string | null> => {
        const dialogs = document.getElementsByClassName('submit-ticket-dialog-root');
        for (const dialog of dialogs) {
            dialog.classList.add('d-none');
        }
        const res = await Utils.screenshotUserBrowserWindow(allowExplicitScreenshot)
        for (const dialog of dialogs) {
            dialog.classList.remove('d-none');
        }
        return res;
    }, [])

    /**
     * Submits a ticket for the error with the given values.
     */
    const submitTicket = useCallback<FinalForm.Config<Schema>["onSubmit"]>(async (values) => {
        setSubmitting(true);
        const response = await ApiService.execute<{ link: string, code: string }>({
            url: EnvService.submitTicketEndpointUrl,
            method: "POST",
            headers: {
                ...(AuthenticationService.authenticatedUser?.token ? {
                    Authorization: AuthenticationService.authenticatedUser.token,
                } : {}),
            },
            data: {
                curl: generateCurl(),
                apiUrl: props.data.requestModel.url,
                resultCode: props.data.responseModel?.code ?? 0,
                resultMessage: props.data.responseModel?.message ?? 0,
                url: window.location.href,
                description: values.description,
                viewSnapshot: await takeScreenshot(values.takeScreenshot),
                screenshot: props.data.responseModel?.viewScreenshot,
            },
            showSubmitTicket: false,
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag && response.data) {
            setSubmissionResult({
                link: response.data.link,
                code: response.data.code,
            });
        }
    }, [generateCurl, props.data.requestModel.url, props.data.responseModel?.code, props.data.responseModel?.message, props.data.responseModel?.viewScreenshot, takeScreenshot, isMounted, setSubmissionResult])

    /**
     * Sets the value for explicitly allowing for screenshots and then submits the form.
     * @param withScreenshot
     */
    const submitForm = useCallback((withScreenshot = false) => {
        if (!formRef.current)
            return;
        formRef.current.change('takeScreenshot', withScreenshot);
        formRef.current.submit();
    }, [])

    return (
        <>
            <DialogContent>
                <Typography variant={'body2'}>
                    We are deeply sorry for your inconvenience. We would encourage you to submit a ticket in case
                    of unexpected results. It helps us improve our systems and ensure a better experience for you.
                    Thanks for your patience and understanding.
                </Typography>
                <Form<Schema, Partial<Schema>>
                    onSubmit={submitTicket}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({form}) => {
                        formRef.current = form;
                        return (
                            <Grid container xs={12} spacing={2} marginBlock={2}>
                                <Grid xs={12}>
                                    <MuiRFF.TextField
                                        multiline
                                        minRows={5}
                                        disabled={submitting}
                                        name={"description"}
                                        label={"Description (optional)"}
                                        required={required.description}
                                    />
                                </Grid>
                                <Box sx={{display: 'none'}}>
                                    <MuiRFF.Checkboxes
                                        name={"takeScreenshot"}
                                        data={{label: "Take Screenshot", value: true}}
                                    />
                                </Box>
                            </Grid>
                        )
                    }}
                </Form>
            </DialogContent>
            <DialogActions sx={{display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant={'text'}
                    type={'button'}
                    color={'primary'}
                    disabled={submitting}
                    onClick={() => submitForm(false)}
                >
                    {
                        submitting
                            ? "Creating..."
                            : "Submit"
                    }
                </Button>
                <Button
                    variant={'text'}
                    type={'button'}
                    color={'primary'}
                    disabled={submitting}
                    onClick={() => submitForm(true)}
                >
                    {
                        submitting
                            ? "Creating..."
                            : "Submit with a Screenshot"
                    }
                </Button>
            </DialogActions>
        </>
    );
}

export default SubmitTicketForm;
